import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    MenuItem,
    Select,
    SelectChangeEvent,
    Tab,
    Tabs,
    TextField,
    Tooltip,
} from "@mui/material";
import styles from './createEscalation.module.css';
import "react-quill/dist/quill.snow.css";
import EmailBodyEditor from "./EmailBodyEditor";
import CloseIcon from '@mui/icons-material/Close';
import IconButton from "@mui/material/IconButton";
import { IEscalationComments, IGetEscalationDialogInfo } from "../../Models/applicationModels";
import { useInsightsUtils } from "../../hooks/useInsightsUtils";
import completedLogo from '../../Assets/Icons/completedMark.svg';
import EmailAutocomplete from "./CreateAutoComplete/EmailAutocomplete";
import { formatUTCDateTime } from "../../utils/formatUTCDate";
import { HistorySection } from './components/historySection/historySection.index';
import { EscalationPopover } from './components/escalationPopover/escalationPopover.index';
import { RAISE_ESCALATION_PLACEHOLDER_TEXT, STATUS_CHANGE_PLACEHOLDER_TEXT } from './createEscalation.constants';
import { SendEmailPayload } from './createEscalation.models';
import { useEscalationInfo } from '../../Context/escalationContext';

interface CreateEscalationProps {
    escalationDialogInfo: IGetEscalationDialogInfo;
    handleCloseEscalationPopUp: (flagUpdateData: boolean, oppId: string) => void;
}

const getInitialCCMails = (combineString: string | null) => {
    if (!combineString) {
        return [];
    }
    return combineString.split(",");
}

// Function to check if all fields are valid
const areAllFieldsValid = (subject: string, bodyValid: boolean, stagedEmailTo: string[]): boolean => {
    return subject.trim() !== "" && bodyValid && stagedEmailTo.length > 0;
};

const CreateEscalation: React.FC<CreateEscalationProps> = (props) => {

    const {
        escalationDialogInfo,
        handleCloseEscalationPopUp
    } = props;

    const { flagUpdateEscalation, setFlagUpdateEscalation } = useEscalationInfo();

    const {
        SaveEscalationData,
        UpdateEscalationStatus
    } = useInsightsUtils()

    const { oppId, escalationId, escalationName, agencyName } = escalationDialogInfo ?? {};

    const { escalationComments } = escalationDialogInfo ?? {};

    const {
        emailSubject,
        emailBody,
        emailTo,
        escalatedBy,
        escalatedAt,
        applicationEscalationUid,
        isEmailSent,
        ccEmails,
        status: InitialStatus,
        availableEscalationStatuses,
    } = escalationDialogInfo?.escalationDetails ?? {};

    const [stagedEmailSubject, setStagedEmailSubject] = useState(emailSubject);
    const [stagedEmailBody, setStagedEmailBody] = useState(emailBody);
    const [flagAllFieldValid, setFlagAllFieldValid] = useState(false);
    const [inputValueTo, setInputValueTo] = React.useState("");
    const [inputValueEmailCC, setInputValueEmailCC] = useState("");
    const [stagedEmailTo, setStagedEmailTo] = React.useState<string[]>(getInitialCCMails(emailTo));
    const [selectedCCEmails, setSelectedCCEmails] = useState<string[]>(getInitialCCMails(ccEmails));
    const [selectedStatus, setSelectedStatus] = useState<string>((InitialStatus ?? ""));
    const [flagBodyValid, setFlagBodyValid] = useState(true);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const selectRef = useRef<HTMLDivElement | null>(null);
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const [anchorElRaiseEscalation, setAnchorElRaiseEscalation] = React.useState<HTMLButtonElement | null>(null);
    const [anchorElStatus, setAnchorElStatus] = React.useState<HTMLElement | null>(null);
    const [pendingStatus, setPendingStatus] = useState<{
        name: string;
        id: number;
    } | null>(null);

    const statusPopoverActionRef = useRef<{ updatePosition: () => void }>(null);
    const raiseEscalationPopoverActionRef = useRef<{ updatePosition: () => void }>(null);
    const sendEmailPopoverActionRef = useRef<{ updatePosition: () => void }>(null);

    const handleStatusChange = (event: SelectChangeEvent<string>) => {
        const newStatus = event.target.value;

        if (newStatus === selectedStatus) return;

        const selectedStatusObj = availableEscalationStatuses.find(
            (status) => status.name === newStatus
        );

        if (!selectedStatusObj) return;

        setPendingStatus({
            name: selectedStatusObj.name,
            id: selectedStatusObj.id,
        });

        setAnchorElStatus(selectRef.current);
    };
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClickRaiseEscalation = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorElRaiseEscalation(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setAnchorElStatus(null);
        setAnchorElRaiseEscalation(null);
    };

    const open = Boolean(anchorEl);
    const openRaiseEscalation = Boolean(anchorElRaiseEscalation);
    const id = open ? 'simple-popover' : undefined;
    const idRaiseEscalation = openRaiseEscalation ? 'simple-popover-raise-escalation' : undefined;
    const openStatus = Boolean(anchorElStatus);
    const idStatus = openStatus ? 'simple-popover-status' : undefined;

    const handleConfirmStatus = async (formData: FormData) => {
        if (!pendingStatus || !applicationEscalationUid) return;

        const res = await UpdateEscalationStatus(
            applicationEscalationUid,
            pendingStatus.id.toString(),
            formData,
            setCommentItems
        );

        if (res) {
            setSelectedStatus(pendingStatus.name);
            setFlagUpdateEscalation(true);
        }

        setPendingStatus(null);
        setAnchorElStatus(null);
    };


    const handleCancelStatus = () => {
        setPendingStatus(null);
        setAnchorElStatus(null);
    };

    useEffect(() => {
        // To focus save button if it is not visible
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const isVisible = (
                rect.top >= 0 &&
                rect.left >= 0 &&
                (rect.bottom + 20) <= (window.innerHeight || document.documentElement.clientHeight) &&
                rect.right <= (window.innerWidth || document.documentElement.clientWidth)
            );
            if (!isVisible) {
                setTimeout(() => {
                    buttonRef.current && buttonRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
                }, 1000)
            }
        }
    }, []);


    useEffect(() => {
        setFlagAllFieldValid(areAllFieldsValid(stagedEmailSubject, flagBodyValid, stagedEmailTo));
    }, [stagedEmailSubject, flagBodyValid, stagedEmailTo]);

    const HandleBodyChange = (value: string) => {
        setStagedEmailBody(value);
    }

    const HandleSaveAndSendMail = async (flagSendMail: boolean, payload: FormData) => {
        if (flagSendMail && !flagAllFieldValid) {
            return;
        }

        const commaSeparatedEmails = selectedCCEmails.join(",");
        const toMailTemp = stagedEmailTo.join(",");

        //These will only call when there is a saved escalation and sending email is remaining and you change the email details body and send email then the put call will be made.
        const flagPutCall = applicationEscalationUid !== null;

        const payloadData = {
            [SendEmailPayload.FLAG_SEND_MAIL]: flagSendMail.toString(),
            [SendEmailPayload.EMAIL_TO]: toMailTemp,
            [SendEmailPayload.EMAIL_BODY]: stagedEmailBody,
            [SendEmailPayload.EMAIL_SUBJECT]: stagedEmailSubject,
            [SendEmailPayload.EMAIL_CC]: commaSeparatedEmails,
        }
        Object.entries(payloadData).forEach(([key, value]) => payload.append(key, value));

        await SaveEscalationData(oppId, escalationId, payload, flagPutCall);
    }

    const emailEditDisable = (applicationEscalationUid !== null && isEmailSent) || InitialStatus === "Closed";
    const flagSaveDisable = InitialStatus === "Closed";

    const [commentItems, setCommentItems] = useState<IEscalationComments[]>(escalationComments ?? []);

    return (
            <Box className={styles.createEscalationCtn}>
                <Box className={styles.header}>
                    <Box className={styles.headerInnerCtn}>
                        <Box>
                            {`${escalationName} - ${agencyName}`}
                        </Box>
                        <Box className={styles.headerRightSide}>
                            {(escalatedBy && escalatedAt) && <Box className={styles.escalationCreationIntoCtn}>
                                <Box className={styles.byAtCtn}>
                                    <p>
                                        Escalated by:
                                    </p>
                                    <p>
                                        {escalatedBy}
                                    </p>
                                </Box>
                                <Box className={styles.verticalLine}>
                                    {/*    Horizontal line*/}
                                </Box>
                                <Box className={styles.byAtCtn}>
                                    <p>
                                        Escalated at:
                                    </p>
                                    <p>
                                        {formatUTCDateTime(escalatedAt, false, true)}
                                    </p>
                                </Box>
                            </Box>}
                            <Box ref={selectRef}>
                                {selectedStatus && <Box>
                                    <Select
                                        value={selectedStatus}
                                        disabled={flagSaveDisable}
                                        onChange={handleStatusChange}
                                        displayEmpty
                                        className={styles.select}
                                        sx={{
                                            "& .MuiSelect-select": {
                                                padding: "8px 10px",
                                            },
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                border: "none",
                                            },
                                            "&:hover > .MuiOutlinedInput-notchedOutline": {
                                                border: "none"
                                            }
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                className: styles.menuPaper,
                                            },
                                        }}>
                                        {availableEscalationStatuses
                                            .map((option) => (
                                                <MenuItem key={option.id} value={option.name}
                                                    className={styles.menuItem}>
                                                    {option.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                    <EscalationPopover
                                        actionRef={statusPopoverActionRef}
                                        open={openStatus}
                                        anchorEl={anchorElStatus}
                                        onClose={handleCancelStatus}
                                        id={idStatus}
                                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                                        transformOrigin={{ vertical: -8, horizontal: "left" }}
                                        confirmProps={{
                                            title: "Change Escalation Status",
                                            placeholderText: STATUS_CHANGE_PLACEHOLDER_TEXT,
                                            onConfirm: handleConfirmStatus,
                                            onCancel: handleCancelStatus,
                                            updateStatus: true,
                                        }}
                                    />
                                </Box>}
                            </Box>
                            <Box>
                                <IconButton
                                    onClick={() => { handleCloseEscalationPopUp(flagUpdateEscalation, oppId); }}
                                    disableRipple
                                    disableFocusRipple
                                    className={styles.closeBtn}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </Box>
                    </Box>
                </Box>
                <Box className={styles.bottomSection}>
                    <Box className={styles.leftBox}>
                        <Box className={styles.bodyHeaderLine}>
                            <Tabs
                                value={0}
                                sx={{
                                    "& .MuiTab-root": {
                                        color: "#000",
                                    },
                                    "& .MuiOutlinedInput-input": {
                                        padding: "10px !important",
                                    },
                                    "& .Mui-selected": {
                                        color: "#3B78CE",
                                        padding: "0px",
                                        minHeight: "unset !important"
                                    },
                                    "& .MuiTabs-indicator": {
                                        backgroundColor: "#3B78CE",
                                        height: "5px",
                                        borderRadius: "5px 5px 0px 0px",
                                    }
                                }}
                                className={styles.tabCtn}>
                                <Tab className={styles.tabTxt} label={"Send Email"} id='simple-tab-0' />
                            </Tabs>
                            <Box>
                                {isEmailSent && <Box className={styles.alreadySentBtnStyles}>
                                    <img src={completedLogo} />
                                    Already Sent
                                </Box>}
                            </Box>
                        </Box>
                        <Box className={styles.bodyCtn}>
                            <Box className={styles.infoTitleLine}>
                                <p className={styles.titleText}>To </p>
                                <EmailAutocomplete
                                    selectedEmails={stagedEmailTo}
                                    setSelectedEmails={setStagedEmailTo}
                                    inputValue={inputValueTo}
                                    setInputValue={setInputValueTo}
                                    disabled={emailEditDisable}
                                    singleEmail={false}
                                />
                            </Box>
                            {(!emailEditDisable || selectedCCEmails.length > 0) ? (
                                <Box className={`${styles.infoTitleLine} ${styles.ccAndSubjects}`}>
                                    <p className={styles.titleText}>CC </p>
                                    <EmailAutocomplete
                                        selectedEmails={selectedCCEmails}
                                        setSelectedEmails={setSelectedCCEmails}
                                        inputValue={inputValueEmailCC}
                                        setInputValue={setInputValueEmailCC}
                                        disabled={emailEditDisable}
                                        singleEmail={false}
                                    />
                                </Box>
                            ) : null}
                            <Box className={`${styles.infoTitleLine} ${styles.ccAndSubjects}`}>
                                <p className={styles.titleText}>Subject: </p>
                                <TextField
                                    className={styles.textFieldValue}
                                    variant="standard"
                                    size="small"
                                    value={stagedEmailSubject}
                                    disabled={emailEditDisable}
                                    onChange={(e) => setStagedEmailSubject(e.target.value)}
                                    InputProps={{
                                        disableUnderline: true, // Ensures underline appears when focused
                                        style: {
                                            background: "transparent", // No background
                                            border: "none",
                                            fontSize: "16px",
                                        },
                                    }}
                                    sx={{
                                        "& .MuiInputBase-root": {
                                            padding: 0,
                                        },
                                        "& .MuiOutlinedInput-root": {
                                            "& fieldset": { border: "none" },
                                        },
                                    }}
                                />
                            </Box>
                            <Box>
                                <EmailBodyEditor
                                    value={stagedEmailBody}
                                    HandleOnChange={HandleBodyChange}
                                    flagDisable={emailEditDisable}
                                    setFlagBodyValid={setFlagBodyValid}
                                />
                            </Box>
                        </Box>
                        {!flagSaveDisable && <Box className={styles.btnTextCtn}>
                            <Box className={styles.btnCtn}>
                                {applicationEscalationUid === null && (
                                    <>
                                        <EscalationPopover
                                            actionRef={raiseEscalationPopoverActionRef}
                                            open={openRaiseEscalation}
                                            anchorEl={anchorElRaiseEscalation}
                                            onClose={handleClose}
                                            id={idRaiseEscalation}
                                            anchorOrigin={{ vertical: -8, horizontal: "left" }}
                                            transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                                            confirmProps={{
                                                title: "Raise Escalation?",
                                                placeholderText: RAISE_ESCALATION_PLACEHOLDER_TEXT,
                                                onConfirm: (payload: FormData) => HandleSaveAndSendMail(false, payload),
                                                onCancel: handleClose,
                                            }}
                                        />
                                        <Tooltip
                                            title="Updated email content won't be saved."
                                            arrow
                                            placement="top"
                                            classes={{
                                                tooltip: styles.customTooltip,
                                                arrow: styles.customTooltipArrow
                                            }}
                                        >
                                            <span>
                                                <Button
                                                    ref={buttonRef}
                                                    variant="outlined"
                                                    onClick={handleClickRaiseEscalation}
                                                >
                                                    Raise
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </>
                                )}

                                {!isEmailSent &&
                                    <>
                                        <EscalationPopover
                                            actionRef={sendEmailPopoverActionRef}
                                            open={open}
                                            anchorEl={anchorEl}
                                            onClose={handleClose}
                                            id={id}
                                            anchorOrigin={{ vertical: -8, horizontal: "left" }}
                                            transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                                            confirmProps={{
                                                title: "Raise & Send Email?",
                                                placeholderText: RAISE_ESCALATION_PLACEHOLDER_TEXT,
                                                onConfirm: (payload: FormData) => HandleSaveAndSendMail(true, payload),
                                                onCancel: handleClose,
                                            }}
                                        />
                                        <Button
                                            onClick={applicationEscalationUid ? () => HandleSaveAndSendMail(true, new FormData()) : handleClick}
                                            aria-describedby={id}
                                            variant="contained"
                                            disabled={!flagAllFieldValid}
                                        >
                                            {applicationEscalationUid ? "Send Email" : "Raise & Send Email"}
                                        </Button>
                                    </>
                                }

                            </Box>
                        </Box>}
                    </Box>
                    <Box className={styles.rightBox}>
                        <HistorySection opportunityUid={oppId} applicationEscalationUid={applicationEscalationUid} commentItems={commentItems} setCommentItems={setCommentItems} initialStatus={InitialStatus} />
                    </Box>
                </Box>
            </Box>
    );
};

export default CreateEscalation;